import React, { useMemo, useState } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import AddIcon from 'icons/add.svg';
import CreateNewFolderIcon from 'icons/create-new-folder.svg';
import EditIcon from 'icons/edit.svg';
import FoldersIcon from 'icons/folders.svg';
import TrashIcon from 'icons/ic-trash.svg';
import SearchOffIcon from 'icons/search-off.svg';
import ThreeDotsIcon from 'icons/three-dots.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import { useFoldersQuery } from 'components/hooks/folderQueries';
import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';

import FilledButton from 'components/application/buttons/FilledButton';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import Paginator from 'components/application/Paginator';
import SearchField from 'components/application/SearchField';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsDashboardContext } from 'components/contexts/ProjectsDashboardContext';
import EmptyTableState from 'components/projectsDashboard/components/EmptyTableState';
import FavouriteFolderToggleButton from 'components/projectsDashboard/components/FavouriteFolderToggleButton';

export default function FoldersIndex({
  defaultCurrentSearch,
  onDeleteClick,
  onFolderClick,
  onNewClick,
  onRenameClick,
}) {
  const {
    division: {
      attributes: { pluralRAMSName },
    },
  } = useCurrentActor();

  const { folderPolicy } = useProjectsDashboardContext();

  const [currentPage, setCurrentPage] = useState(1);

  const [currentSearch, setCurrentSearch, handleSearchInputChange] = useForm({
    folderSearch: defaultCurrentSearch || '',
  });

  const [debouncedCurrentSearch] = useDebounce(currentSearch, 250, {
    onChange: () => setCurrentPage(1),
  });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.FoldersIndex',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const {
    data: domainFolders,
    isError: isDomainFoldersFetchError,
    isFetching: isDomainFoldersFetching,
    isLoading: isDomainFoldersLoading,
    isSuccess: isDomainFoldersFetchSuccess,
  } = useFoldersQuery({
    params: {
      page: currentPage,
      search: debouncedCurrentSearch.folderSearch,
    },
    queryOptions: { keepPreviousData: true },
  });

  const canAction = folderPolicy.canDestroy || folderPolicy.canUpdate;

  const buildColumnsArgs = {
    canAction,
    pluralRAMSName,
    translations: {
      headers: { folder: i18nTCommon('projects.folder') },
      options: {
        ...i18nT('columns.options', { returnObjects: true }),
        delete: i18nTCommon('projects.deleteFolder'),
      },
    },
  };

  const columns = useMemo(
    () => buildColumns(buildColumnsArgs),
    [JSON.stringify(buildColumnsArgs)],
  );

  const isDataPending =
    isDomainFoldersLoading ||
    isDomainFoldersFetching ||
    isDomainFoldersFetchError;

  const domainFoldersData = useMemo(
    () =>
      isDataPending ?
        Array(domainFolders?.data.length || 25).fill({})
      : domainFolders?.data || [],
    [isDataPending, domainFolders],
  );

  const table = useReactTable({
    columns,
    data: domainFoldersData,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    meta: {
      isDataPending,
      onDeleteClick,
      onFolderClick,
      onRenameClick,
      ...(canAction ? { folderPolicy } : {}),
    },
  });

  const isSearchFieldDisabled =
    isDomainFoldersFetchSuccess &&
    !debouncedCurrentSearch.folderSearch &&
    domainFolders.data.length === 0;

  return (
    <div className='tw-flex tw-flex-col tw-gap-6'>
      <div className='tw-flex tw-flex-col tw-gap-1 tw-px-4'>
        <div className='tw-flex tw-h-10 tw-items-center tw-justify-between'>
          <div className='tw-flex tw-items-center tw-gap-1'>
            <FoldersIcon
              className='[&_path]:tw-fill-grey-500'
              height={20}
              width={20}
            />
            <p className='tw-mb-0 tw-px-1.5 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              {i18nTCommon('projects.folders')}
            </p>
          </div>
          {folderPolicy.canCreate && (
            <FilledButton color='mint' onClick={onNewClick}>
              <div className='tw-flex tw-gap-2'>
                <AddIcon
                  className='[&_path]:tw-fill-grey-900'
                  height={20}
                  width={20}
                />
                {i18nTCommon('projects.newFolder')}
              </div>
            </FilledButton>
          )}
        </div>
        <SearchField
          additionalClasses={classNames(
            'tw-w-70',
            isSearchFieldDisabled ? 'tw-bg-grey-025' : 'tw-bg-white',
          )}
          isDisabled={isSearchFieldDisabled}
          name='folderSearch'
          onChange={handleSearchInputChange}
          onReset={() => {
            setCurrentSearch({ ...currentSearch, folderSearch: '' });
          }}
          placeholder={i18nT('search.placeholder')}
          value={currentSearch.folderSearch}
        />
      </div>
      {(
        isDomainFoldersFetchSuccess &&
        !debouncedCurrentSearch.folderSearch &&
        domainFolders.data.length === 0
      ) ?
        folderPolicy.canCreate ?
          <EmptyTableState
            icon={CreateNewFolderIcon}
            title={i18nT('emptyState.creatable.title')}
          >
            <div className='tw-flex tw-flex-col tw-items-center tw-gap-4'>
              <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
                {i18nT('emptyState.creatable.description', {
                  interpolation: { escapeValue: false },
                  pluralRAMSName,
                })}
                &nbsp;
                <a
                  className='tw-text-m tw-font-medium tw-text-blue-500'
                  href='https://intercom.help/hands-hq/en/articles/11154401-folders-organise-and-group-your-rams'
                  rel='noreferrer'
                  target='_blank'
                >
                  {i18nTCommon('shared.learnMore')}
                </a>
              </p>
              <FilledButton color='mint' onClick={onNewClick}>
                <div className='tw-flex tw-gap-2'>
                  <AddIcon
                    className='[&_path]:tw-fill-grey-900'
                    height={20}
                    width={20}
                  />
                  {i18nTCommon('projects.newFolder')}
                </div>
              </FilledButton>
            </div>
          </EmptyTableState>
        : <EmptyTableState
            icon={FoldersIcon}
            title={i18nT('emptyState.readOnly.title')}
          >
            <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
              {i18nT('emptyState.readOnly.description')}
            </p>
          </EmptyTableState>

      : <div className='tw-flex tw-flex-col tw-gap-2'>
          <div className='tw-flex tw-h-8 tw-items-center tw-px-4'>
            <p className='tw-mb-0 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-700'>
              {i18nT('title', { count: domainFolders?.meta.scopedCount || 0 })}
            </p>
          </div>
          {isDataPending || domainFolders.data.length > 0 ?
            <>
              <table className='tw-table-fixed tw-border-separate tw-select-none tw-border-1 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-grey-100 tw-bg-white'>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          className={classNames(
                            'tw-bg-grey-050',
                            header.column.columnDef.meta?.classNames,
                          )}
                          key={header.id}
                        >
                          <div
                            className={classNames(
                              'tw-flex tw-h-10 tw-items-center tw-justify-start tw-truncate tw-p-2 tw-text-s tw-font-medium tw-tracking-auto tw-text-grey-900',
                              header.column.getIsFirstColumn() ?
                                'tw-pl-4 tw-pr-2'
                              : 'tw-px-2',
                            )}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr
                      className='tw-group/row tw-bg-white hover:tw-bg-blue-025'
                      key={row.id}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          className={classNames(
                            'tw-h-13 tw-border-0 tw-border-t-1 tw-border-solid tw-border-grey-100',
                            cell.column.columnDef.meta?.classNames,
                          )}
                          key={cell.id}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {isDomainFoldersFetchSuccess &&
                domainFolders.meta.totalPages > 1 && (
                  <div className='tw-mt-12 tw-text-center'>
                    <Paginator
                      currentPage={domainFolders.meta.currentPage}
                      onClick={(event) =>
                        setCurrentPage(
                          event.currentTarget.getAttribute('data-page'),
                        )
                      }
                      totalPages={domainFolders.meta.totalPages}
                    />
                  </div>
                )}
            </>
          : <EmptyTableState
              icon={SearchOffIcon}
              title={i18nT('noResults.title')}
            >
              <p className='tw-mb-0 tw-text-center tw-text-m tw-font-normal tw-text-grey-900'>
                {i18nT('noResults.description')}
              </p>
            </EmptyTableState>
          }
        </div>
      }
    </div>
  );
}

FoldersIndex.propTypes = {
  defaultCurrentSearch: PropTypes.string,
  onDeleteClick: PropTypes.func.isRequired,
  onFolderClick: PropTypes.func.isRequired,
  onNewClick: PropTypes.func.isRequired,
  onRenameClick: PropTypes.func.isRequired,
};

function buildColumns({ canAction, pluralRAMSName, translations }) {
  return [
    {
      id: 'folder',
      header: translations.headers.folder,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending)
          return (
            <div className='tw-p-2'>
              <TextSkeleton />
            </div>
          );

        const folderId = row.original.id;

        const favouriteUserFolderId =
          row.original.relationships.favouriteUserFolder?.data?.id;

        return (
          <div className='tw-flex'>
            <div className='tw-p-2'>
              <FavouriteFolderToggleButton
                favouriteUserFolderId={favouriteUserFolderId}
                folderId={folderId}
              />
            </div>
            <button
              className='tw-w-full tw-truncate tw-border-none tw-bg-transparent tw-px-2 tw-text-left tw-font-medium'
              onClick={() => {
                table.options.meta.onFolderClick({ id: folderId });
              }}
            >
              {row.original.attributes.name}
            </button>
          </div>
        );
      },
    },
    {
      id: 'projectCount',
      accessorFn: (folder) => folder.id,
      header: voca.capitalize(pluralRAMSName),
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <button
            className='tw-w-full tw-border-none tw-bg-transparent tw-p-2 tw-text-left tw-font-normal'
            onClick={() => {
              table.options.meta.onFolderClick({ id: row.original.id });
            }}
          >
            {row.original.meta.projectCount}
          </button>
        );
      },
      meta: {
        classNames: 'tw-w-50 min-w-1280:tw-w-90',
      },
    },
    canAction && {
      id: 'actions',
      header: '',
      cell: ({ row, table }) => {
        const {
          folderPolicy: { canDestroy, canUpdate },
          isDataPending,
          onDeleteClick,
          onRenameClick,
        } = table.options.meta;

        if (isDataPending) return null;

        return (
          <div className='tw-p-2'>
            <Options
              align='right'
              btnToggle={(isOpen) => (
                <div
                  className={classNames(
                    'tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg',
                    isOpen ?
                      'tw-border-1 tw-border-solid tw-border-blue-100 tw-bg-white'
                    : 'hover:tw-bg-blue-050',
                  )}
                >
                  <ThreeDotsIcon
                    className='[&_path]:tw-fill-grey-500 [&_path]:group-hover/row:tw-fill-grey-700'
                    width={12}
                  />
                  <span className='tw-sr-only'>
                    {translations.options.toggle}
                  </span>
                </div>
              )}
              listStyle='tw-w-54 [&_li]:tw-h-auto [&_li]:tw-border-none'
              toggleStyle='[&_ul]:tw-border-blue-100 [&_ul]:tw-bg-grey-025 [&_ul]:tw-rounded-lg'
            >
              {canUpdate && (
                <OptionChoice>
                  <button
                    className='tw-group tw-flex tw-w-full tw-gap-2 tw-text-wrap tw-rounded-tl-lg tw-rounded-tr-lg tw-border-none tw-bg-white tw-p-3 tw-text-left tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-bg-blue-025'
                    onClick={() => onRenameClick({ id: row.original.id })}
                  >
                    <EditIcon
                      className='[&_path]:tw-fill-grey-700'
                      height={20}
                      width={20}
                    />
                    {translations.options.rename}
                  </button>
                </OptionChoice>
              )}
              {canDestroy && (
                <OptionChoice>
                  <button
                    className='tw-group tw-flex tw-w-full tw-gap-2 tw-text-wrap tw-rounded-bl-lg tw-rounded-br-lg tw-border-none tw-bg-white tw-p-3 tw-text-left tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-bg-red-025 hover:tw-text-red-700'
                    onClick={() => onDeleteClick({ id: row.original.id })}
                  >
                    <TrashIcon
                      className='[&_path]:tw-fill-grey-700 [&_path]:group-hover:tw-fill-red-700'
                      height={20}
                      width={20}
                    />
                    {translations.options.delete}
                  </button>
                </OptionChoice>
              )}
            </Options>
          </div>
        );
      },
      meta: {
        classNames: 'tw-w-12',
      },
    },
  ].filter(Boolean);
}
